import { Card, CardContent, CircularProgress, makeStyles, TextField, Typography, Select, InputLabel, FormControl, MenuItem } from "@material-ui/core";
import { Stack } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import {
  getAnalysisData,
  getKpisByRideId,
  getRecalculatedKpis,
  getOutcomeTextsByRideId,
  getRideById,
  saveAnalysis,
  saveCorrectSensorData,
  saveCorrectSensorDataRange,
  startAnalysis,
} from "../../store/actions/RideActions";
import GraphGeneratorForAnalyze from "../GraphGeneratorForAnalyze";
import OutcomeTextsHandler from "./../OutcomeTextsHandler";
import { ACTION_TYPES as USER_ACTION_TYPES } from "../../store/reducers/UserReducer";
import { ACTION_TYPES as RIDE_ACTION_TYPES } from "../../store/reducers/RideReducer";
import { ACTION_TYPES as UTILS_ACTION_TYPES } from "../../store/reducers/UtilsReducer";
import { useBetween } from "use-between";
import { useShareableState } from "../utils/SharableStates";
import { getSensorDataAsCsv } from "../../store/actions/SensorDataActions";
import { getUserByRideId } from "../../store/actions/UserActions";
import { evaluateGaitTypeSwe, RISING_TROT, SITTING_TROT } from "../../utils/AppUtils";

// Memoized graph component to prevent unnecessary re-renders
const MemoizedGraphGenerator = React.memo(GraphGeneratorForAnalyze);

const useStyles = makeStyles((theme) => {
  return {
    root: {
      padding: "4rem 0px",
      display: "flex",
      flexDirection: "column",
      minHeight: "80vh",
      alignItems: "center",
      position: "relative",
    },
    content: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    rideInfoContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      width: "18rem",
      margin: "auto",
    },
    cardWrapper: {
      marginTop: "2.5rem",
      width: "100%",
    },
    cardContent: {
      padding: "1rem 0 2rem 0",
    },
    kpiAndGraphWrapper: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      marginBottom: "5rem",
      "@media (min-width: 1068px)": {
        flexDirection: "row",
      },
    },
    kpi: {
      border: "solid lightGray 2px",
      borderRadius: 10,
      padding: "1rem 2rem",
      marginBottom: "2rem",
      marginRight: "1rem",
      marginLeft: "1rem",
      "@media (min-width: 1068px)": {
        marginTop: "17rem",
        marginBottom: "0",
        marginRight: "1rem",
        width: "28%",
        height: "34rem",
      },
    },
    graphWrapper: {
      width: "100%",
      "@media (min-width: 1068px)": {
        width: "67%",
      },
    },
    graphImage: {
      maxWidth: "50rem",
    },
    adjustingCheckboxContainer: {
      display: "flex",
      justifyContent: "center",
      margin: "auto",
    },
    checkboxWrapper: {
      padding: "1.5rem 2rem",
      display: "flex",
      alignItems: "center",
    },
    checkbox: {
      width: "1.4rem",
      height: "1.4rem",
    },
    labelAll: {
      color: "rgb(245 179 53)",
      fontWeight: "bold",
    },
    textX: {
      color: "red",
      fontWeight: "bold",
    },
    textY: {
      color: "blue",
      fontWeight: "bold",
    },
    textZ: {
      color: "#a6a6a6",
      fontWeight: "bold",
    },
    switchingButtonContainer: {
      display: "flex",
      justifyContent: "center",
      margin: "auto",
    },
    buttonWrapper: {
      display: "flex",
      justifyContent: "center",
      padding: "2rem 1rem",
    },
    switchButton: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "4rem",
      border: "2px solid rgb(245 179 53)",
      borderRadius: "0.3rem",
      backgroundColor: "transparent",
      fontWeight: "bold",
      cursor: "pointer",
      "&:hover": {
        color: "rgb(245 179 53)!important",
      },
    },
    button: {
      padding: "0.7rem 0.5rem",
      width: "9rem",
      color: "white",
      backgroundColor: "rgb(245 179 53)",
      textDecoration: "none",
      fontSize: ".975rem",
      borderRadius: "0.7rem",
      fontWeight: "500",
      cursor: "pointer",
      border: "none",
      marginLeft: "1rem",
      marginRight: "1rem",
      "&:hover": {
        color: "#a6a6a6",
      },
    },
  };
});

function DoAnalysis({
  loading,
  getKpisByrideId,
  getOutcomeTextsByrideId,
  getAnalysisData,
  analysisData,
  kpis,
  outcomeTexts,
  saveAnalysis,
  sensorDataIndex,
  saveCorrectSensorData,
  success,
  getSensorDataAsCsv,
  csvFile,
  startAnalysis,
  reloading,
  getUserByRideId,
  user,
  getRideById,
  ride,
}) {
  const IntervalTypes = {
    DATA_POINTS: "Datapunkter",
    TIMESTAMP: "Tidstämpel",
  };

  const classes = useStyles();
  const { rideId } = useParams();
  const navigate = useNavigate();
  const [showAllData, setShowAllData] = useState(false);
  const [changeXdata, setChangeXData] = useState(false);
  const [changeYdata, setChangeYData] = useState(false);
  const [changeZdata, setChangeZData] = useState(false);
  const [switchXAndY, setSwitchXAndY] = useState(false);
  const [switchXAndZ, setSwitchXAndZ] = useState(false);
  const [switchYAndZ, setSwitchYAndZ] = useState(false);
  const [selectedIntervalType, setSelectedIntervalType] = useState(
    IntervalTypes.DATA_POINTS,
  );
  const [overrideIndexStart, setOverrideIndexStart] = useState("");
  const [overrideIndexEnd, setOverrideIndexEnd] = useState("");
  const [currentSensorDataIndex, setCurrentSensorDataIndex] = useState(sensorDataIndex);
  const dispatch = useDispatch();
  const { sensorData, setSensorData } = useBetween(useShareableState);
  const [reset, setReset] = useState(false);

  useEffect(() => {
    setCurrentSensorDataIndex(sensorDataIndex);
  }, [sensorDataIndex]);

  useEffect(() => {
    getAnalysisData(rideId, navigate);
    getKpisByrideId(rideId, navigate);
    getOutcomeTextsByrideId(rideId, navigate);
    getUserByRideId(rideId, navigate);
    getRideById(rideId, navigate);

    return () => {
      dispatch({ type: RIDE_ACTION_TYPES.CLEAR_ANALYSIS_DATA });
      dispatch({ type: RIDE_ACTION_TYPES.CLEAR_KPIS });
      dispatch({ type: RIDE_ACTION_TYPES.CLEAR_OUTCOME_TEXTS });
      dispatch({ type: USER_ACTION_TYPES.CLEAR_USER });
      dispatch({ type: RIDE_ACTION_TYPES.CLEAR_RIDE });
    };
  }, []);

  useEffect(() => {
    if (!reloading) {
      getAnalysisData(rideId, navigate);
      getKpisByrideId(rideId, navigate);
      getOutcomeTextsByrideId(rideId, navigate);
    }
  }, [reloading]);

  useEffect(() => {
    if (overrideIndexStart === "") {
      setOverrideIndexEnd("");
      return;
    }
    if (selectedIntervalType !== IntervalTypes.TIMESTAMP) {
      if (!isNaN(Number(overrideIndexStart))) {
        setOverrideIndexEnd(Number(overrideIndexStart) + 500);
      }
    } else {
      // Check if input matches the expected timestamp format (e.g., "00:00:00")
      const timestampRegex = /^(\d{2}):(\d{2}):(\d{2})$/;
      const match = overrideIndexStart.match(timestampRegex);
      if (!match) {
        setOverrideIndexEnd("");
        return;
      }

      // Parse out the timestamp to unix time in milliseconds

      // Get ride date
      const rideDate = new Date(ride.createdAt);
      console.log("Ride date:", rideDate);
      // Create a date object using today's date and the input time
      const inputTime = new Date(
        rideDate.getFullYear(),
        rideDate.getMonth(),
        rideDate.getDate(),
        parseInt(match[1]), // hours
        parseInt(match[2]), // minutes
        parseInt(match[3]), // seconds
      );

      const inputTimeInMs = inputTime.getTime();
      // Add 10s to the input time
      const endTimeInMs = inputTimeInMs + 1000 * 10;

      // Convert endTimeInMs back to a timestamp string in the same format
      const endTime = new Date(endTimeInMs);
      const hours = String(endTime.getHours()).padStart(2, "0");
      const minutes = String(endTime.getMinutes()).padStart(2, "0");
      const seconds = String(endTime.getSeconds()).padStart(2, "0");

      setOverrideIndexEnd(`${hours}:${minutes}:${seconds}`);
    }
  }, [overrideIndexStart, selectedIntervalType]);

  const handleCheckboxChecked = (e) => {
    setReset(false);
    const targetName = e.target.name;
    if (targetName === "showAll") {
      setShowAllData(!showAllData);
    }
    if (targetName === "changeX") {
      setChangeXData(!changeXdata);
    }
    if (targetName === "changeY") {
      setChangeYData(!changeYdata);
    }
    if (targetName === "changeZ") {
      setChangeZData(!changeZdata);
    }
  };

  const handleSwapClick = (e) => {
    setReset(false);
    const targetName = e.target.name;
    if (targetName === "x-to-y") {
      setSwitchXAndY(!switchXAndY);
    }
    if (targetName === "x-to-z") {
      setSwitchXAndZ(!switchXAndZ);
    }
    if (targetName === "y-to-z") {
      setSwitchYAndZ(!switchYAndZ);
    }
  };

  const handleSaveChanges = () => {
    setReset(false);
    setShowAllData(false);
    const requestBody = {
      sensorDataList: sensorData,
    };
    saveCorrectSensorData(rideId, requestBody, navigate);
    dispatch({ type: UTILS_ACTION_TYPES.START_RELOADING });
    dispatch({ type: RIDE_ACTION_TYPES.CLEAR_ANALYSIS_DATA });
    dispatch({ type: RIDE_ACTION_TYPES.CLEAR_KPIS });
    dispatch({ type: RIDE_ACTION_TYPES.CLEAR_OUTCOME_TEXTS });
  };

  const handleResetChanges = (e) => {
    setReset(true);
    setSensorData(analysisData);
    document.getElementById("changeX").checked = false;
    document.getElementById("changeY").checked = false;
    document.getElementById("changeZ").checked = false;

    setChangeXData(false);
    setChangeYData(false);
    setChangeZData(false);
  };

  const handleFileDownload = () => {
    getSensorDataAsCsv(rideId, navigate);
  };

  const handleGetRecalculatedKpis = () => {
    if (overrideIndexStart === "" || overrideIndexEnd === "") {
      alert("Ange ett giltigt startindex för att beräkna KPI i intervall.");
      return;
    }

    let calculatedIndexStart = overrideIndexStart;

    if (selectedIntervalType === IntervalTypes.TIMESTAMP) {
      const timestampRegex = /^(\d{2}):(\d{2}):(\d{2})$/;
      const match = overrideIndexStart.match(timestampRegex);
      if (!match || !sensorData?.length) {
        alert("Ogiltig tidstämpel eller saknad sensordata.");
        return;
      }

      const hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);
      const second = parseInt(match[3], 10);

      const rideDate = new Date(ride.createdAt);
      const inputTime = new Date(
        rideDate.getFullYear(),
        rideDate.getMonth(),
        rideDate.getDate(),
        hour,
        minute,
        second,
      );

      // Normalize epoch values to ms so both ms and scientific-notation seconds work.
      const toEpochMs = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return NaN;
        return Math.abs(n) < 1e11 ? n * 1000 : n; // seconds -> ms, ms stays ms
      };

      const inputTimeMs = inputTime.getTime();
      console.log(
        "Input timestamp:",
        overrideIndexStart,
        "Input time (ms):",
        inputTimeMs,
      );

      let left = 0;
      let right = sensorData.length - 1;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const midTimeMs = toEpochMs(sensorData[mid].timeIntervalSince1970);

        console.log("Binary search - mid:", mid, "midTimeMs:", midTimeMs);

        if (!Number.isFinite(midTimeMs)) {
          console.warn("Invalid time at index:", mid);
          break;
        }

        if (midTimeMs < inputTimeMs) {
          left = mid + 1;
        } else if (midTimeMs > inputTimeMs) {
          right = mid - 1;
        } else {
          calculatedIndexStart = mid;
          console.log("Exact match found at index:", mid);
          break;
        }
      }

      // No exact match -> first index greater than input timestamp.
      if (calculatedIndexStart === overrideIndexStart) {
        calculatedIndexStart = Math.min(left, sensorData.length - 1);
        console.log("No exact match. Closest index:", calculatedIndexStart);
      }

      console.log("Final calculatedIndexStart:", calculatedIndexStart);
    }

    if (Number(calculatedIndexStart) + 500 >= sensorData.length) {
      alert(
        "Intervall utanför datamängdens gränser. (Max startindex: " +
          (sensorData.length - 501) +
          ")",
      );
      return;
    }

    setCurrentSensorDataIndex({startIndex: Number(calculatedIndexStart), endIndex: Number(calculatedIndexStart) + 500});
    setShowAllData(false);

    const requestBody = {
      ride_id: Number(rideId),
      biometrics: {
        mass: ride.weight,
        height: ride.height,
      },
      window: {
        mode: "manual",
        n_start: Number(calculatedIndexStart),
        n_end: Number(calculatedIndexStart) + 500,
      },
    };
    dispatch(getRecalculatedKpis(requestBody, navigate));
  };

  return (
    <div className={classes.root}>
      {!loading &&
      !reloading &&
      analysisData.length &&
      kpis.length &&
      outcomeTexts.length &&
      user &&
      ride ? (
        <div className={classes.content}>
          <div>
            <Typography variant="h1" align="center">
              Rider's Positions analyseringsverktyg
            </Typography>
            <Typography gutterBottom variant="body1" align="center">
              Tryck på "Spara analys" längst ner på sidan när du känner dig nöjd
              med resultatet.
            </Typography>
            <div className={classes.rideInfoContainer}>
              <Typography variant="body1">
                <strong>Ryttare:</strong> &nbsp;{user.userName}
              </Typography>
              <Typography variant="body1">
                <strong>Gångart:</strong> &nbsp;
                {evaluateGaitTypeSwe(ride.rideGaitType)}
              </Typography>
              <Typography variant="body1">
                <strong>Språk:</strong> &nbsp;{ride.locale}
              </Typography>
            </div>
          </div>
          <Card elevation={0} className={classes.cardWrapper}>
            <CardContent className={classes.cardContent}>
              <div className={classes.kpiAndGraphWrapper}>
                <div className={classes.kpi}>
                  <Typography gutterBottom variant="h6">
                    KPI:er
                  </Typography>
                  <div>
                    {kpis &&
                      kpis.map((kpi, index) => {
                        return (
                          <Typography variant="body1" gutterBottom key={index}>
                            <strong>{kpi.kpiName}:</strong> {kpi.kpiValue.toFixed(2)}
                          </Typography>
                        );
                      })}
                  </div>
                </div>
                <div className={classes.graphWrapper}>
                  <div className={classes.adjustingCheckboxContainer}>
                    <div className={classes.checkboxWrapper}>
                      <input
                        className={classes.checkbox}
                        type="checkbox"
                        name="showAll"
                        checked={showAllData}
                        onChange={(e) => handleCheckboxChecked(e)}
                      />
                      <label className={classes.labelAll} htmlFor="showAll">
                        Se all data
                      </label>
                    </div>
                    <div className={classes.checkboxWrapper}>
                      <input
                        className={classes.checkbox}
                        type="checkbox"
                        name="changeX"
                        id="changeX"
                        onChange={(e) => handleCheckboxChecked(e)}
                      />
                      <label className={classes.textX} htmlFor="changeX">
                        Justera X
                      </label>
                    </div>
                    <div className={classes.checkboxWrapper}>
                      <input
                        className={classes.checkbox}
                        type="checkbox"
                        name="changeY"
                        id="changeY"
                        onChange={(e) => handleCheckboxChecked(e)}
                      />
                      <label className={classes.textY} htmlFor="changeY">
                        Justera Y
                      </label>
                    </div>
                    <div className={classes.checkboxWrapper}>
                      <input
                        className={classes.checkbox}
                        type="checkbox"
                        name="changeZ"
                        id="changeZ"
                        onChange={(e) => handleCheckboxChecked(e)}
                      />
                      <label className={classes.textZ} htmlFor="changeZ">
                        Justera Z
                      </label>
                    </div>
                  </div>
                  <div className={classes.switchingButtonContainer}>
                    <div className={classes.buttonWrapper}>
                      <button
                        className={classes.switchButton}
                        type="button"
                        name="x-to-y"
                        onClick={(e) => handleSwapClick(e)}
                      >
                        X &#8646; Y
                      </button>
                    </div>
                    <div className={classes.buttonWrapper}>
                      <button
                        className={classes.switchButton}
                        type="button"
                        name="x-to-z"
                        onClick={(e) => handleSwapClick(e)}
                      >
                        X &#8646; Z
                      </button>
                    </div>
                    <div className={classes.buttonWrapper}>
                      <button
                        className={classes.switchButton}
                        type="button"
                        name="y-to-z"
                        onClick={(e) => handleSwapClick(e)}
                      >
                        Y &#8646; Z
                      </button>
                    </div>
                  </div>
                  <div className={classes.buttonWrapper}>
                    <button onClick={() => handleSaveChanges()} className={classes.button}>
                      Spara ändringar
                    </button>
                    <button onClick={(e) => handleResetChanges(e)} className={classes.button}>
                      Återställ ändringar
                    </button>
                    <button onClick={() => handleFileDownload()} className={classes.button}>
                      Hämta CSV-fil
                    </button>
                  </div>

                  <div className={classes.range}>
                    <Typography align="center" variant="body1">
                      <strong>Beräkna KPI i intervall</strong>
                    </Typography>
                    <div
                      className={classes.indexContainer}
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "1rem",
                        display: "flex",
                      }}
                    >
                      <FormControl
                        variant="filled"
                        style={{ width: "max-content", minWidth: 180 }}
                      >
                        <InputLabel>
                          Inmatningsmetod
                        </InputLabel>
                        <Select
                          value={selectedIntervalType}
                          onChange={(e) => {
                            setSelectedIntervalType(e.target.value);
                            setOverrideIndexStart(""); // Reset the start index when changing interval type
                            setOverrideIndexEnd(""); // Reset the end index when changing interval type
                          }}
                          autoWidth
                        >
                          <MenuItem value={IntervalTypes.DATA_POINTS}>
                            Datapunkter
                          </MenuItem>
                          <MenuItem value={IntervalTypes.TIMESTAMP}>
                            Tidstämpel
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <div>
                        <TextField
                          id="start-index"
                          label="Startindex"
                          variant="filled"
                          type={
                            selectedIntervalType === IntervalTypes.TIMESTAMP
                              ? "text"
                              : "number"
                          }
                          value={overrideIndexStart}
                          onChange={(e) => {
                            const value = e.target.value;
                            setOverrideIndexStart(value);
                          }}
                        />
                      </div>
                      <div>
                        <TextField
                          id="end-index"
                          label="Slutindex"
                          variant="filled"
                          disabled={true}
                          value={overrideIndexEnd}
                          InputLabelProps={{
                            shrink: overrideIndexStart !== "",
                          }}
                        />
                      </div>
                      <button
                        onClick={() => handleGetRecalculatedKpis()}
                        className={classes.button}
                        style={{ marginLeft: "-3px" }}
                      >
                        Beräkna
                      </button>
                    </div>
                  </div>
                  <MemoizedGraphGenerator
                    reset={reset}
                    showAllData={showAllData}
                    changeXdata={changeXdata}
                    changeYdata={changeYdata}
                    changeZdata={changeZdata}
                    switchXAndY={switchXAndY}
                    switchXAndZ={switchXAndZ}
                    switchYAndZ={switchYAndZ}
                    graphData={analysisData}
                    sensorDataIndex={currentSensorDataIndex}
                  />
                </div>
              </div>
              <OutcomeTextsHandler outcomeTexts={outcomeTexts} saveAnalysis={saveAnalysis} />
            </CardContent>
          </Card>
        </div>
      ) : (
        <Stack
          sx={{
            color: "grey.500",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          spacing={2}
          direction="row"
        >
          <CircularProgress color="inherit" />
        </Stack>
      )}
    </div>
  );
}

const mapStateToProps = ({ ride, sensorData, utils, user }) => ({
  loading: ride.loading,
  analysisData: ride.analysisData,
  kpis: ride.kpis,
  response: ride.response,
  success: ride.success,
  outcomeTexts: ride.outcomeTexts,
  sensorDataIndex: ride.sensorDataIndex,
  csvFile: sensorData.csvFile,
  reloading: utils.reloading,
  user: user.user,
  ride: ride.ride,
});

const mapDispatchToProps = (dispatch, navigate) => {
  return {
    getKpisByrideId: (rideId, navigate) => dispatch(getKpisByRideId(rideId, navigate)),
    getRecalculatedKpis: (request, navigate) => dispatch(getRecalculatedKpis(request, navigate)),
    getOutcomeTextsByrideId: (rideId, navigate) => dispatch(getOutcomeTextsByRideId(rideId, navigate)),
    getAnalysisData: (rideId, navigate) => dispatch(getAnalysisData(rideId, navigate)),
    saveAnalysis: (request, navigate) => dispatch(saveAnalysis(request, navigate)),
    saveCorrectSensorData: (rideId, request, navigate) => dispatch(saveCorrectSensorData(rideId, request, navigate)),
    getSensorDataAsCsv: (rideId, navigate) => dispatch(getSensorDataAsCsv(rideId, navigate)),
    getUserByRideId: (rideId, navigate) => dispatch(getUserByRideId(rideId, navigate)),
    startAnalysis: (rideId, navigate) => dispatch(startAnalysis(rideId, navigate)),
    getRideById: (rideId, navigate) => dispatch(getRideById(rideId, navigate)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DoAnalysis);
